import { useCallback, useRef, useState } from 'react';

export function dateInIstanbul(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat('tr-TR', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(Date.now() + offsetDays * 86400000));
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export const firstQuickLesson = lessons => lessons[0] || '';
export const validQuickDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
  && !Number.isNaN(Date.parse(`${value}T12:00:00Z`))
  && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value && value <= dateInIstanbul();

// Surface adapters authenticate and load options. Both layouts share local
// selection rules and a single final POST; saved homework autosave is separate.
export default function useQuickHomeworkEvaluation({ students, send, onCompleted, onError }) {
  const [lesson, setLesson] = useState('');
  const [assignedDate, setAssignedDate] = useState(() => dateInIstanbul(-1));
  const [evaluations, setEvaluations] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);
  const lock = useRef(false);
  const activeStudents = students.filter(student => !student.isAbsent);
  const absentCount = students.length - activeStudents.length;
  const evaluatedCount = activeStudents.filter(student => evaluations[student.id]).length;
  const evaluatedAbsentCount = students.filter(student => student.isAbsent && evaluations[student.id]).length;
  const remainingCount = activeStudents.length - evaluatedCount;
  const selectedStudents = students.filter(student => evaluations[student.id]);
  const reset = useCallback(() => {
    setLesson(''); setAssignedDate(dateInIstanbul(-1)); setEvaluations({});
    setFormError(''); setConfirmIncomplete(false);
  }, []);
  const setStatus = (id, status) => {
    if (lock.current) return;
    setEvaluations(current => ({ ...current, [id]: current[id] === status ? '' : status }));
    setFormError(''); setConfirmIncomplete(false);
  };
  const markAllDone = () => {
    if (lock.current) return;
    setEvaluations(current => ({ ...current, ...Object.fromEntries(activeStudents.filter(student => !current[student.id]).map(student => [student.id, 'done'])) }));
    setFormError(''); setConfirmIncomplete(false);
  };
  const submit = async ({ confirmed = false, valid = true, payload } = {}) => {
    if (lock.current) return;
    if (!valid || !lesson || !validQuickDate(assignedDate)) { setFormError('Ders ve geçerli bir tarih seçin. Gelecek tarih kullanılamaz.'); return; }
    if (!selectedStudents.length) { setFormError('Göndermek için en az bir öğrencinin durumunu seçin.'); return; }
    if (remainingCount > 0 && !confirmed) { setConfirmIncomplete(true); return; }
    lock.current = true; setSaving(true); setConfirmIncomplete(false); setFormError('');
    try {
      const result = await send(payload);
      if (!result?.homework?.id || !Number.isInteger(result.evaluated) || result.evaluated < 1) throw new Error('Kayıt sonucu doğrulanamadı.');
      await onCompleted?.(result);
    } catch (error) {
      const message = error.message || 'Değerlendirme kaydedilemedi.';
      setFormError(message); onError?.(message);
    } finally { lock.current = false; setSaving(false); }
  };
  return { lesson, setLesson, assignedDate, setAssignedDate, evaluations, setEvaluations,
    saving, locked: saving, formError, setFormError, confirmIncomplete, setConfirmIncomplete,
    activeStudents, absentCount, evaluatedCount, evaluatedAbsentCount, remainingCount, selectedStudents,
    setStatus, markAllDone, submit, reset };
}
